// imports
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import Config from '$root/Config';
import Model from '$dependencies/Model';

// private static properties
const FIELDS = {
	id: {
		type: Number,
		default: '',
		identifier: true,
	},

	// login response
	uid: {
		type: Number,
		default: -1,
		setter: false,
	},
	jwt_token: {
		type: String,
		default: '',
		getter: false,
		setter: false,
	},
	refresh_token: {
		type: String,
		default: '',
	},
	expiration: {
		type: Date,
		default: () => new Date(),
		setter: false,
	},
	created: {
		type: Date,
		default: () => new Date(),
		setter: false,
	},
	// user info response
};

// class definition
export default class SessionModel extends Model {
	// constructor
	constructor(args = {}) {
		// clone args to do some modifications
		args = Object.assign({}, args);
		args.fields = FIELDS;

		// call super constructor
		super(args);

		// set extra property
		this._authenticated = false;
		this.rememberLoggedIn = false;

		// set headers config
		this.setConfig('headers', {
			'cache-control': 'no-cache',
		});

		// check for autoload => if true => load from local
		if (args.autoLoad) {
			this.readLocal();
		}
	}

	// methods
	login(username, password, rememberLoggedIn, client_id, client_secret) {
		this.rememberLoggedIn = rememberLoggedIn;

		// prep model config for jwt request
		this.prepareConfig();

		const params = new URLSearchParams();
		params.append('grant_type', 'password');
		params.append('client_id', client_id);
		params.append('client_secret', client_secret);
		params.append('username', username);
		params.append('password', password);

		this.setConfig({
			url: Config.getUrl('login'),
			headers: {
				'Content-type': 'application/x-www-form-urlencoded',
			},
			data: params,
		});

		// send post
		return this.post({});
	}

	requestPassword(email) {
		this.prepareConfig();

		this.setConfig({
			url: Config.getUrl('requestPassword'),
			data: {
				mail: email,
			},
			params: {
				_format: 'json',
			},
			withCredentials: true,
		});

		// send post
		this.post({});
	}

	resetPassword(userName, tempPass, newPass) {
		this.prepareConfig();

		this.setConfig({
			url: Config.getUrl('resetPassword'),
			data: {
				name: userName,
				temp_pass: tempPass,
				new_pass: newPass,
			},
			params: {
				_format: 'json',
			},
			withCredentials: true,
		});

		// send post
		this.post({});
	}

	logout() {
		this.clear();
		this.clearLocal();
		this.emit('logout');
	}

	response(xhr) {
		if (xhr.status === 200) {
			if (xhr.data.access_token && xhr.data.refresh_token) {
				this.setToken(xhr.data.access_token);
				this.setRefreshToken(xhr.data.refresh_token);
			} else if (_includes(_get(xhr, 'data.message'), 'instructions')) {
				this.emit('requestPassword.success');
			} else if (_includes(_get(xhr, 'data.message'), 'saved')) {
				this.emit('resetPassword.success');
			} else {
				this.error();
			}
		}
	}

	prepareConfig() {
		this.unsetConfig('headers');
		this.unsetConfig('params');
		this.unsetConfig('withCredentials');
	}

	setToken(token) {
		this.jwt_token = token;
		this.writeLocal();
	}

	setRefreshToken(refreshToken) {
		this.refresh_token = refreshToken;

		const payload = JSON.stringify({
			refresh_token: this.refresh_token,
		});
		const { refreshToken: refreshTokenKey } = Config.storageKeys;

		// Always available for the current browser tab
		sessionStorage.setItem(refreshTokenKey, payload);

		// Use the in-memory flag only — never re-read vuex here (that overwrote true → false on login)
		if (this.rememberLoggedIn) {
			localStorage.setItem(refreshTokenKey, payload);
		} else {
			localStorage.removeItem(refreshTokenKey);
		}
	}

	writeLocal() {
		const { session } = Config.storageKeys;
		const sessionData = JSON.stringify({
			jwt_token: this.jwt_token,
			rememberLoggedIn: !!this.rememberLoggedIn,
		});

		// Always available for the current browser tab
		sessionStorage.setItem(session, sessionData);

		// Use the in-memory flag only — never re-read vuex here (that overwrote true → false on login)
		if (this.rememberLoggedIn) {
			localStorage.setItem(session, sessionData);
		} else {
			localStorage.removeItem(session);
		}
	}

	// eslint-disable-next-line class-methods-use-this
	clearLocal() {
		const { session, refreshToken } = Config.storageKeys;

		localStorage.removeItem(session);
		sessionStorage.removeItem(session);
		localStorage.removeItem(refreshToken);
		sessionStorage.removeItem(refreshToken);
	}

	readLocal() {
		this.hydrateRememberFlag();

		const storage = this.rememberLoggedIn ? localStorage : sessionStorage;
		const savedSession = storage.getItem(Config.storageKeys.session);

		if (!savedSession) {
			return;
		}

		try {
			const parsedSession = JSON.parse(savedSession);

			if (parsedSession && parsedSession.jwt_token) {
				this.jwt_token = parsedSession.jwt_token;
			}
		} catch (e) {
			// ignore malformed session payload
		}
	}

	hydrateRememberFlag() {
		// 1. Session payload in localStorage is the source of truth after a "stay logged in" login
		const localSession = localStorage.getItem(Config.storageKeys.session);
		if (localSession) {
			try {
				const parsedSession = JSON.parse(localSession);
				if (parsedSession && parsedSession.jwt_token) {
					this.rememberLoggedIn = true;
					return;
				}
			} catch (e) {
				// continue to other sources
			}
		}

		// 2. Fall back to vuex-persisted remember flag
		const vuexStorage = localStorage.getItem('vuex');
		if (vuexStorage) {
			try {
				const parsedVuex = JSON.parse(vuexStorage);
				this.rememberLoggedIn = !!_get(parsedVuex, 'session.rememberLoggedIn');
			} catch (e) {
				// ignore malformed vuex payload
			}
		}

		// 3. Authorized-origin handoff still counts as remember
		const isAuthorizedOrigin = _includes(document.referrer, window.origin) && document.referrer !== window.origin;
		if (isAuthorizedOrigin) {
			this.rememberLoggedIn = true;
		}
	}

	getStorageOption() {
		this.hydrateRememberFlag();
		return this.rememberLoggedIn ? localStorage : sessionStorage;
	}

	destroy() {}

	// utility methods

	// getters & setters
	get jwt_token() {
		return this.get('jwt_token');
	}

	set jwt_token(token) {
		if (token !== this.jwt_token) {
			// set token
			this.set({ jwt_token: token });

			// parse & set token
			parseJwtData.call(this, token);
		}
	}
}

function parseJwt(token) {
	const base64Url = token.split('.')[1];
	const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

	return JSON.parse(window.atob(base64));
}

function parseJwtData(token) {
	const data = parseJwt(token);

	this.set({
		uid: data.sub,
		expiration: data.exp,
		created: data.iat,
	});
}
